export type LoadingStatus = LoadableContent<unknown, unknown>['status'];
export type UppercaseLoadingStatus = Uppercase<LoadingStatus>;

export type WorkflowStatus = 'INIT' | 'IN_PROGRESS' | 'SUCCESS' | 'FAILED' | 'REJECTED';

export type LoadableContent<T, Error = string> = SuccessContent<T> | IdleContent | LoadingContent | ErrorContent<Error>;
export type SuccessContent<T> = {
  status: 'success';
  content: T;
};
type IdleContent = {
  status: 'idle';
};
type LoadingContent = {
  status: 'loading';
};
type ErrorContent<Error> = {
  status: 'error';
  error: Error;
};

export type LoadableResult<Error = string> = {
  status: LoadingStatus;
  error?: Error;
};

/**
 * Determines whether a loading operation can be retried based on its current status.
 *
 * @param status - The current loading status to check
 * @returns `true` if the status is either 'error' or 'idle', indicating the operation can be retried; `false` otherwise
 *
 * @example
 * ```typescript
 * canBeRetried('error'); // returns true
 * canBeRetried('idle');  // returns true
 * canBeRetried('loading'); // returns false
 * ```
 */
export function canBeRetried(status: LoadingStatus): boolean {
  return status === 'error' || status === 'idle';
}

/**
 * Converts a loading status to 'loading' if it can be retried.
 *
 * This function checks whether a given loading status allows for a retry operation.
 * If the status can be retried, it returns 'loading' to indicate that a new attempt
 * should be made. Otherwise, it returns the original status unchanged.
 *
 * @param status - The current loading status to evaluate
 * @returns 'loading' if the status can be retried, otherwise returns the original status
 *
 * @example
 * ```typescript
 * // If status is 'error' and can be retried
 * toLoadingWhenCanBeRetried('error'); // returns 'loading'
 *
 * // If status is 'success' and cannot be retried
 * toLoadingWhenCanBeRetried('success'); // returns 'success'
 * ```
 */
export function toLoadingWhenCanBeRetried(status: LoadingStatus): LoadingStatus {
  return canBeRetried(status) ? 'loading' : status;
}
export function isSuccess<T, Error>(val: LoadableContent<T, Error>): val is SuccessContent<T> {
  return val.status === 'success';
}
export function isError<T, Error>(val: LoadableContent<T, Error>): val is ErrorContent<Error> {
  return val.status === 'error';
}

export function asSuccess<T, Error>(val: LoadableContent<T, Error>): SuccessContent<T> | undefined {
  if (isSuccess(val)) {
    return val;
  }
  return undefined;
}

export function asError<T, Error>(val: LoadableContent<T, Error>): ErrorContent<Error> | undefined {
  if (isError(val)) {
    return val;
  }
  return undefined;
}
